<script setup lang="ts">
import { computed } from "vue";
import { useRouter } from "vue-router";

import theCard from "@/components/theCard.vue";
import { useCardBookmark } from "@/composables/useCardBookmark";
import { useCardTelemetry } from "@/composables/useCardTelemetry";
import isUnauth from "@/utils/isUnauth";

const props = withDefaults(
  defineProps<{
    result: any;
    //eslint-disable-next-line
    index?: number;
    status?: any;
    forCarousel?: boolean;
    cardView?: string;
    cardStyle?: string;
    topicView?: boolean;
    showDescription?: boolean;
  }>(),
  {
    index: 0,
    status: null,
    forCarousel: false,
    cardView: "Grid",
    cardStyle: "Grid",
    topicView: false,
    showDescription: true,
  }
);

const router = useRouter();

// Composables
const {
  bookmarkMe,
  debouncedBookmarkMe,
  addContentToMyList,
  removeContentFromMyList,
} = useCardBookmark();

const { sendTelemetry: sendTelemetryBase } = useCardTelemetry({
  transformData: (baseData) => ({
    ...baseData,
    url: props.result.url, // Use raw URL instead of resultLink for events
  }),
  onSuccess: () => {
    if (props.result.url.startsWith("https://")) {
      window.open(resourceUrl.value, "_blank", "noopener");
    } else {
      router.push({ name: "error" });
    }
  },
});

// Computed properties
const resourceUrl = computed(() => {
  const url = props.result.catalog_data?.url || props.result.url;
  return !url || url.length < 5 ? props.result.id : url;
});

// Methods
const sendTelemetry = async () => {
  await sendTelemetryBase(props, ""); // Empty resultLink since we use props.result.url
};

// Expose methods for testing
defineExpose({
  addContentToMyList,
  removeContentFromMyList,
  bookmarkMe,
  debouncedBookmarkMe,
  sendTelemetry,
  resourceUrl,
});
</script>

<template>
  <div
    data-cy="event-result"
    v-show="result"
    :class="[
      'relative z-10 h-full hover:z-[700] focus:z-[700] active:z-[700]',
      {
        'w-[19rem]': props.cardView === 'Grid' && !topicView,
        'w-full': props.cardView !== 'Grid' && !topicView,
        'w-[22rem]': topicView,
      },
    ]"
  >
    <the-card
      :is-unauth="isUnauth()"
      :for-carousel="forCarousel"
      :card-view="props.cardView"
      :card-style="props.cardStyle"
      :result="result"
      :status="status"
      :link="resourceUrl"
      :cy="'event-card'"
      :show-description="props.showDescription"
      @navigate="sendTelemetry"
      @bookmark="debouncedBookmarkMe(result)"
    />
  </div>
</template>
